import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { LeaveType } from '@/services/leaveManagement/leave.types';
import { useUpdateLeaveType } from '@/services/leaveManagement/LeaveTypesProvider';
import { FunctionComponent, useState } from 'react';
import { IoClose, IoRefresh } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminDeactivateLeaveTypeCardProps {
  leaveType: LeaveType;
}

const config = {
  active: {
    title: adminStrings.leaveTypeSection.deactivateReactivate.deactivate,
    icon: IoClose,
    info: adminStrings.leaveTypeSection.deactivateReactivate.deactivateInfo,
    buttonText: adminStrings.common.deactivate,
    confirmTitle: adminStrings.leaveTypeSection.deactivateReactivate.confirmTitle,
    confirmBody: adminStrings.leaveTypeSection.deactivateReactivate.confirmDeactivateBody,
    successMessage: adminStrings.leaveTypeSection.deactivateReactivate.successDeactivate,
    errorMessage: adminStrings.leaveTypeSection.deactivateReactivate.errorDeactivate
  },
  inactive: {
    title: adminStrings.leaveTypeSection.deactivateReactivate.reactivate,
    icon: IoRefresh,
    info: adminStrings.leaveTypeSection.deactivateReactivate.reactivateInfo,
    buttonText: adminStrings.common.reactivate,
    confirmTitle: adminStrings.leaveTypeSection.deactivateReactivate.confirmReactivateTitle,
    confirmBody: adminStrings.leaveTypeSection.deactivateReactivate.confirmReactivateBody,
    successMessage: adminStrings.leaveTypeSection.deactivateReactivate.successReactivate,
    errorMessage: adminStrings.leaveTypeSection.deactivateReactivate.errorReactivate
  }
} as const;

const AdminDeactivateReactivateTaskCard: FunctionComponent<AdminDeactivateLeaveTypeCardProps> = ({ leaveType }) => {
  const { title, icon, info, buttonText, confirmTitle, confirmBody, successMessage, errorMessage } =
    config[leaveType.active ? 'active' : 'inactive'];

  const { fetching, update } = useUpdateLeaveType();
  const navigate = useNavigate();

  const onSubmit = async () => {
    const result = await update({ ...leaveType, active: !leaveType.active });
    await handleMutationResult(result, {
      successMessage,
      errorMessage,
      onSuccess: () => navigate('/admin/leave-types')
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <TSTitleCard title={title(leaveType.name)} icon={icon} className="w-full">
      <div className="flex flex-row items-center space-x-3">
        <Button onClick={() => setIsModalOpen(true)} loading={fetching} danger tint>
          {buttonText}
        </Button>
        <p>{info}</p>
      </div>

      <ConfirmDialog
        onConfirm={onSubmit}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={confirmTitle(leaveType.name)}
        confirmLabel={buttonText}
        cancelLabel={adminStrings.common.cancel}
      >
        {confirmBody}
      </ConfirmDialog>
    </TSTitleCard>
  );
};

export default AdminDeactivateReactivateTaskCard;
